import { Nav, NavBody, NavFooter, NavList, Sidebar, SidebarToggle } from '@optiaxiom/react';
import { IconAngleRight } from '@optimizely/axiom-icons';
import { useDecision } from '@optimizely/react-sdk';

import { NavigationItem } from '@frontend/components/ui/NavigationItem';
import { useLocalStorage, useOperations } from '@frontend/hooks';
import { OperationsName } from '@frontend/types';

import {
  IconAgentRegular,
  IconBookRegular,
  IconCommentsRegular,
  IconDatabaseRegular,
  IconGearRegular,
  IconScrewdriverWrenchRegular,
  IconToggleOffRegular,
} from '../icons';

type Route = {
  path: string;
  icon: JSX.Element;
  label: string;
  permission: OperationsName;
  featureFlag?: string;
  footerNav?: boolean;
};

export const LeftNavbar = () => {
  const { canUser, operations } = useOperations();
  const [sidebarExpanded, setSidebarExpanded] = useLocalStorage('sidebar-expanded', false);

  const [appearance_settings_decision] = useDecision('appearance_settings');

  const routes: Route[] = [
    {
      path: 'chat',
      icon: <IconCommentsRegular />,
      label: 'Chat',
      permission: 'allow_chat',
    },
    {
      path: 'instructions',
      icon: <IconBookRegular />,
      label: 'Instructions',
      permission: 'manage_instructions',
    },
    {
      path: 'agents',
      icon: <IconAgentRegular />,
      label: 'Agents',
      permission: 'manage_instances',
    },
    {
      path: 'tools',
      icon: <IconScrewdriverWrenchRegular />,
      label: 'Tools',
      permission: 'manage_instructions',
    },
    {
      path: 'rag',
      icon: <IconDatabaseRegular />,
      label: 'RAG',
      permission: 'manage_instructions',
      featureFlag: 'shamwow-settings-page',
    },
    {
      path: 'connections',
      icon: <IconToggleOffRegular />,
      label: 'Connections',
      permission: 'manage_instances',
    },
    {
      path: 'settings',
      icon: <IconGearRegular />,
      label: 'Settings',
      permission: appearance_settings_decision.enabled ? 'allow_chat' : 'manage_instances',
    },
  ] as const;

  const allowedOperations = operations
    .filter(operation => operation.allowed)
    .map(operation => operation.name);

  const hasOneOperation = allowedOperations.length === 1;

  // for now we hide left navbar if user has only one operation and FF for settings page is disabled
  // will remove when FF is removed
  if (hasOneOperation && !appearance_settings_decision.enabled) {
    return null;
  }

  const renderNavItem = (route: Route) => (
    <NavigationItem
      key={route.path}
      path={route.path}
      icon={route.icon}
      label={route.label}
      permission={route.permission}
      featureFlag={route.featureFlag}
    />
  );

  const renderNavItems = ({ isFooterNav }: { isFooterNav: boolean }) =>
    routes
      .filter(route => canUser(route.permission) && Boolean(route.footerNav) === isFooterNav)
      .map(renderNavItem);

  return (
    <Sidebar defaultExpanded={sidebarExpanded} onExpandedChange={setSidebarExpanded}>
      <Nav>
        <NavBody>
          <NavList>{renderNavItems({ isFooterNav: false })}</NavList>
        </NavBody>

        <NavFooter>
          <NavList>
            <SidebarToggle icon={<IconAngleRight />} />
            {renderNavItems({ isFooterNav: true })}
          </NavList>
        </NavFooter>
      </Nav>
    </Sidebar>
  );
};
